import type { MeView } from "./app/queries.ts";

/**
 * The authenticated document's `GET /me`, adopted from the request `public/prime.js` started in
 * the head.
 *
 * `main.tsx` cannot render until `GET /me` answers, because the body decides between the shell
 * and the sign-in screen and seeds the cache the shell reads
 * (`contracts/client-api.md` § Session lifecycle). Sent from `main.tsx` the request could not
 * begin until the whole initial module graph had arrived and evaluated: on the slow profile of
 * `contracts/screens.md` § Cold load it left the connection idle while the browser waited, and
 * every request the shell makes started behind it (2026-09-21). Started from the head it is in
 * flight one round trip after the document and has usually answered by the time the bundle runs.
 *
 * The priming script is a plain asset rather than a second module script of the document,
 * because Vite bundles every module script of one HTML entry into that entry's chunk and the
 * request would be back where it started. It carries no knowledge of the response, so a cached
 * copy cannot disagree with this build: the status and the body are read here.
 *
 * `null` means there is nothing to adopt — no priming script, a transport failure, or a status
 * that is neither 200 nor 401 — and the caller falls back to the API client.
 */
export interface PrimedSession {
  readonly status: 200 | 401;
  readonly me: MeView | null;
}

declare global {
  interface Window {
    /** Set by `public/prime.js`; absent when the priming script did not run. */
    readonly __batonPrimedMe?: Promise<Response | null>;
  }
}

export async function primedSession(): Promise<PrimedSession | null> {
  const pending = window.__batonPrimedMe;
  if (pending === undefined) return null;
  try {
    const response = await pending;
    if (response === null) return null;
    if (response.status === 401) return { status: 401, me: null };
    if (response.status !== 200) return null;
    return { status: 200, me: (await response.json()) as MeView };
  } catch {
    return null;
  }
}
